import React, { useState } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { CanvasFrame, CanvasPathStep } from '../types';

interface PathStepRow extends CanvasPathStep {
  _id: string;
}

let nextRowId = 0;
function makeRowId(): string {
  nextRowId += 1;
  return `path-step-${nextRowId}`;
}

interface Props {
  path: CanvasPathStep[];
  frames: CanvasFrame[];
  onChange: (path: CanvasPathStep[]) => void;
}

/**
 * Herordenbare lijst voor board.path -- dnd-kit (al geïnstalleerd, al
 * gebruikt in WriterStudioPage.jsx voor hoofdstuk-herordening), zelfde
 * patroon: PointerSensor + KeyboardSensor voor toetsenbord-toegankelijkheid.
 * CanvasPathStep-objecten hebben zelf geen stabiele id (kunnen hetzelfde
 * frame meermaals bevatten) -- vandaar een lokaal toegekende _id puur voor
 * dnd-kit's identiteitstracking, nooit meegestuurd naar de API.
 */
export default function CanvasPathEditor({ path, frames, onChange }: Props) {
  const [steps, setSteps] = useState<PathStepRow[]>(() => path.map((step) => ({ ...step, _id: makeRowId() })));
  const [newTargetId, setNewTargetId] = useState<string>(frames[0]?.id || '');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const commit = (next: PathStepRow[]) => {
    setSteps(next);
    onChange(next.map(({ _id, ...step }) => step));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = steps.findIndex((s) => s._id === active.id);
    const newIndex = steps.findIndex((s) => s._id === over.id);
    commit(arrayMove(steps, oldIndex, newIndex));
  };

  const addOverviewStep = () => commit([...steps, { _id: makeRowId(), type: 'overview', target_id: null }]);
  const addFocusStep = () => {
    if (!newTargetId) return;
    commit([...steps, { _id: makeRowId(), type: 'focus', target_id: newTargetId }]);
  };
  const removeStep = (id: string) => commit(steps.filter((s) => s._id !== id));

  return (
    <div className="space-y-2">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={steps.map((s) => s._id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-1">
            {steps.length === 0 && <p className="text-xs text-slate-500">Nog geen presentatiepad.</p>}
            {steps.map((step, index) => (
              <PathStepRowItem key={step._id} step={step} index={index} frames={frames} onRemove={() => removeStep(step._id)} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button type="button" variant="outline" size="sm" onClick={addOverviewStep}>
          <Plus className="mr-1 h-3.5 w-3.5" /> Overzicht-stap
        </Button>
        <Select value={newTargetId} onValueChange={setNewTargetId}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Frame kiezen" /></SelectTrigger>
          <SelectContent>
            {frames.map((f) => <SelectItem key={f.id} value={f.id}>{f.title || f.id}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button type="button" variant="outline" size="sm" onClick={addFocusStep} disabled={!newTargetId}>
          <Plus className="mr-1 h-3.5 w-3.5" /> Focus-stap
        </Button>
      </div>
    </div>
  );
}

interface RowProps {
  step: PathStepRow;
  index: number;
  frames: CanvasFrame[];
  onRemove: () => void;
}

function PathStepRowItem({ step, index, frames, onRemove }: RowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step._id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const frame = step.target_id ? frames.find((f) => f.id === step.target_id) : null;
  const label = step.type === 'overview' ? 'Overzicht' : (frame?.title || step.target_id || 'Onbekend frame');

  return (
    <div
      ref={setNodeRef} style={style}
      className="flex items-center gap-1 rounded-md border border-slate-800 bg-slate-900 px-2 py-1.5 text-sm text-slate-200"
    >
      <button
        type="button" {...attributes} {...listeners} aria-label={`Versleep stap ${index + 1}`}
        className="cursor-grab touch-none p-0.5 text-slate-500 hover:text-slate-300 active:cursor-grabbing"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>
      <span className="flex-1 truncate">{index + 1}. {label}</span>
      <Button type="button" variant="ghost" size="icon" onClick={onRemove} aria-label="Stap verwijderen">
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
